<template>
  <div v-if="canDisplayBreadcrumbs">
    <template v-if="homeLayout">
      <v-breadcrumbs
        v-if="itemsList.length > 1"
        class="pa-0 flex-wrap breadcrumbs-home"
      >
        <template v-for="(item, i) in itemsList.slice(0, -1)" :key="`item-${i}`">
          <v-breadcrumbs-item
            :class="item.id !== (paginatedDocumentsCurrentFolderId || currentProjectsFolder.id) ? 'clickable' : ''"
            class="px-0"
            @click="handleGoTo(item)"
          >
            <span class="text-grey-700 font-weight-regular text--x-small">{{ item.name }}</span>
          </v-breadcrumbs-item>

          <v-breadcrumbs-divider v-if="i < itemsList.length - 2">
            <app-icon-ds icon="chevron-right" size="xxs" />
          </v-breadcrumbs-divider>
        </template>
      </v-breadcrumbs>

      <div
        v-if="itemsList.length"
        class="font-weight-semi-bold text-grey-900 mt-1"
        style="font-size: 24px;"
      >
        {{ itemsList[itemsList.length - 1].name }}
      </div>
    </template>

    <v-breadcrumbs v-else class="pa-0 flex-wrap">
      <template v-for="(item, i) in itemsList" :key="`item-${i}`">
        <AppDroppableArea
          :disabled="isFolderDropDisabled(item, i)"
          @dragenter="dragEnterParentFolder(item, i)"
          @dragleave="dragLeaveParentFolder"
          @ondrop="dropDocumentsInParentFolder($event, item)"
        >
          <v-breadcrumbs-item
            class="py-1 px-3"
            :class="item.id !== (paginatedDocumentsCurrentFolderId || currentProjectsFolder.id) ? 'clickable' : ''"
            @click="handleGoTo(item)"
          >
            <span v-if="numberingEnabled && item.numbering" class="pr-1">{{ item.numbering }}</span>
            <span>{{ item.name }}</span>
          </v-breadcrumbs-item>
        </AppDroppableArea>
        <v-breadcrumbs-divider v-if="i < itemsList.length - 1" :key="`divider-${i}`">
          <font-awesome-icon :icon="['fal', 'chevron-right']"/>
        </v-breadcrumbs-divider>
      </template>
    </v-breadcrumbs>
  </div>
  <v-skeleton-loader v-else width="260px" type="text" />
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'

import AppDroppableArea from '@/common/AppDroppableArea.vue'
import { flatten } from '@/common/utils/flatArray'
import { toast } from '@/common/utils/toast'
import { FileToUpload, FolderToUpload } from '@/models/documentToUpload.model'
import { FOLDER_ROUTE_NAME, ROOMS_ROUTE_NAME } from '@/router/index'
import { AppUploadHandlerMutationTypes } from '@/store/modules/app-upload-handler/mutation_types'
import { DESELECT_ALL_DOCUMENTS, LOAD_FOLDERS_TREE, MOVE_FILDERS } from '@/store/modules/documents/action_types'
import { SET_MOVEMENT_SNACKBAR_IS_OPEN } from '@/store/modules/documents/mutation_types'
import { DocumentsBreadcrumbActionTypes } from '@/store/modules/documents-breadcrumb/action_types'
import { START_UPLOAD } from '@/store/modules/documents-upload-files/action_types'
import { ADD_FILES } from '@/store/modules/documents-upload-files/mutation_types'

export default {
  name: 'DocumentsBreadcrumb',
  components: { AppDroppableArea },
  props: {
    items: {
      type: Array,
      default: () => ([]),
    },
  },
  data () {
    return {
      draggedFolder: null,
      FOLDER_ROUTE_NAME,
      ROOMS_ROUTE_NAME,
    }
  },
  computed: {
    ...mapState('global', ['homeLayout']),
    ...mapState('projectsFolders', ['currentProjectsFolder', 'projectsFolderByIdPending']),
    ...mapGetters('documents', ['isRootFolder']),
    ...mapState('documents', ['documentsToMove', 'foldersTree', 'loading', 'getFolderPathPending', 'numberingEnabled', 'paginatedDocumentsCurrentFolderId']),
    ...mapState('documentsBreadcrumb', ['breadcrumb']),
    ...mapGetters('room', ['currentUserRights', 'roomMnemo']),
    canDisplayBreadcrumbs () {
      if (this.homeLayout) {
        return !this.projectsFolderByIdPending
      }
      return !this.loading && !this.getFolderPathPending
    },
    itemsList () {
      return this.homeLayout ? this.items : this.breadcrumb
    },
  },
  watch: {
    breadcrumb: {
      handler () {
        this.DESELECT_ALL_DOCUMENTS()
      },
      deep: true,
    },
    draggedFolder (folder) {
      if (folder) {
        this.openUploadHint(folder.name)
      } else {
        this.closeUploadHint()
      }
    },
  },
  beforeUnmount () {
    this.RESET_BREADCRUMB()
  },
  methods: {
    ...mapActions('documentsBreadcrumb', [DocumentsBreadcrumbActionTypes.GOTO, DocumentsBreadcrumbActionTypes.RESET_BREADCRUMB]),
    ...mapActions('documents', [DESELECT_ALL_DOCUMENTS, LOAD_FOLDERS_TREE]),
    ...mapMutations('appUploadHandler', [AppUploadHandlerMutationTypes.SET_UPLOAD_HINT]),
    ...mapActions('documents', [MOVE_FILDERS]),
    ...mapActions('documentsUploadFiles', [START_UPLOAD]),
    ...mapMutations('documentsUploadFiles', [ADD_FILES]),
    ...mapMutations('documents', [SET_MOVEMENT_SNACKBAR_IS_OPEN]),
    handleGoTo (item) {
      if (this.homeLayout && item.id !== this.currentProjectsFolder.id) {
        this.navigateToProjectsFolder(item)
      } else if (!this.homeLayout && item.id !== this.paginatedDocumentsCurrentFolderId) {
        this.GOTO(item)
      }
    },
    navigateToProjectsFolder (item) {
      if (item.id) {
        this.$router.push({ name: FOLDER_ROUTE_NAME, params: { folderId: item.id } })
      } else {
        this.$router.push({ name: ROOMS_ROUTE_NAME })
      }
    },
    dragEnterParentFolder (folder, index) {
      if (this.homeLayout) return
      if (!this.isFolderDropDisabled(folder, index)) {
        this.draggedFolder = folder
      }
    },
    dragLeaveParentFolder () {
      if (this.homeLayout) return
      this.draggedFolder = null
    },
    dropDocumentsInParentFolder ($event, destinationFolder) {
      const documentFromClosd = $event.dataTransfer.getData('fromClosd') === 'true'
      if (documentFromClosd) {
        const documentId = Number($event.dataTransfer.getData('documentId'))
        const documentType = $event.dataTransfer.getData('documentType')
        const dropFolderInItself = documentId === destinationFolder.id && documentType === destinationFolder.type

        if (!dropFolderInItself) {
          this.moveClosdDocument(destinationFolder)
        }
      } else {
        this.dropLocalDocuments($event, destinationFolder)
      }
    },
    openUploadHint (destinationFolder) {
      this.SET_UPLOAD_HINT({
        visible: true,
        destinationFolder: destinationFolder,
      })
    },
    closeUploadHint () {
      this.SET_UPLOAD_HINT({ visible: false })
    },
    async moveClosdDocument (targetFolder) {
      try {
        await this.MOVE_FILDERS({
          documents: this.documentsToMove.documents,
          targetFolderId: targetFolder.id,
        })
        this.SET_MOVEMENT_SNACKBAR_IS_OPEN(true)
      } catch {
        toast.error(this.$t('project.documents.DocumentsBreadcrumb.moveError'))
      }

      if (this.documentsToMove.documents.some(doc => doc.type === 'folder')) {
        await this.LOAD_FOLDERS_TREE()
      }
      if (this.documentsToMove.isDragElementSelected) {
        this.DESELECT_ALL_DOCUMENTS()
      }
    },
    async dropLocalDocuments ($event, targetFolder) {
      const dataTransferItemList = $event.dataTransfer ? $event.dataTransfer.items : []
      const documentsToUpload = []
      const queue = []
      dataTransferItemList.forEach(item => {
        queue.push(item.webkitGetAsEntry())
      })

      for (const entry of queue) {
        if (entry.isFile) {
          const fileToUpload = await this.generateFileToUpload(entry, targetFolder.id)
          documentsToUpload.push(fileToUpload)
        }

        if (entry.isDirectory) {
          if (!this.currentUserRights.canCreateFolders) {
            toast.error(this.$t('project.documents.DocumentsBreadcrumb.noFolderAsGuest'))
            return
          }

          if (this.hasTodoFolderOrChild) {
            toast.error(this.$t('project.documents.DocumentsBreadcrumb.noFolderInChecklist'))
            return
          }

          const folderToUpload = await this.generateFolderToUpload(entry, targetFolder.id)
          documentsToUpload.push(folderToUpload)
        }
      }

      this.ADD_FILES(documentsToUpload)
      this.START_UPLOAD()
    },
    async generateFolderToUpload (folder, parentFolderId) {
      const folderToUpload = new FolderToUpload(folder.name, [], parentFolderId)

      let readEntries
      try {
        readEntries = await new Promise((resolve, reject) => {
          folder.createReader().readEntries(resolve, reject)
        })
      } catch (err) {
        console.error(err)
      }

      for (const entry of readEntries) {
        if (entry.isFile) {
          folderToUpload.children.push(await this.generateFileToUpload(entry))
          continue
        }

        if (entry.isDirectory) {
          folderToUpload.children.push(await this.generateFolderToUpload(entry))
        }
      }

      return folderToUpload
    },
    async generateFileToUpload (file, parentFolderId) {
      let entryFile
      try {
        entryFile = await new Promise((resolve, reject) => {
          file.file(resolve, reject)
        })
      } catch (err) {
        console.error(err)
      }

      return new FileToUpload(entryFile, 'document', parentFolderId)
    },
    isFolderDropDisabled (folder, index) {
      if (this.documentsToMove.isDragFromFoldersTree && !this.canDropFromFolderTree(folder)) {
        return true
      }
      return index === this.breadcrumb.length - 1 || folder.hasOwnProperty('specialFolder')
    },
    canDropFromFolderTree (targetFolder) {
      if (!targetFolder || !this.documentsToMove.isDragFromFoldersTree) {
        return false
      }
      const flattenFolders = flatten([], this.foldersTree?.folders)
      const matchingFolder = flattenFolders.find(flattenFolder => flattenFolder.id === this.documentsToMove.documents[0].id)
      const childrenOfMatchingFolder = flatten([], matchingFolder?.children)
      const dropInSameParent = this.documentsToMove.originalFolderId === targetFolder.id
      const dropToSameFolder = this.documentsToMove.documents[0].id === targetFolder.id
      const dropInChildrenFolder = childrenOfMatchingFolder.some(childFolder => childFolder.id === targetFolder.id)
      return !dropInSameParent && !dropToSameFolder && !dropInChildrenFolder
    },
  },
}
</script>

<style scoped>
.breadcrumbs-home .clickable:hover {
  text-decoration: underline;
}
</style>
